import { IDropdownItem } from '@/interfaces/storageTracking/IDropdownItem.ts';
import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import { IFilters } from '@/interfaces/storageTracking/IFilters.ts';
import { IDecoratedDropdownItem } from '@/interfaces/general/IDecoratedDropdownItem.ts';
import { findCategoryColourCode } from '@/enums/storageTracking/Colours.ts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { FiltersApi } from '@/api/storageTracking/FiltersApi.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';

export const useFilterDataStore = defineStore('filtersDataStore', () => {
    // If changing this, change the backend too (MarketTrends Min and Max year)
    const startDate = 2018;
    const sliderStartDate = 2016;
    const endDate = 2030;
    const sliderEndDate = 2030;

    const anyValueStreamValue = 'anyValueStream';

    const projectTypeOptions: Array<IIdName> = [
        { id: '1', name: 'Actual Projects', info: 'Named projects and deployed aggregates' },
        { id: '2', name: 'Overall Forecast', info: 'Total expected deployment of storage projects' },
    ];

    const operationalOptions: IDecoratedDropdownItem[] = [
        {
            name: 'All Projects',
            id: 'AA',
            icon: 'asterisk',
            color: findCategoryColourCode('AA'),
        },
        {
            name: 'Currently operational',
            id: 'Operational',
            icon: 'battery-bolt',
            color: findCategoryColourCode('Operational'),
        },
        {
            name: 'In development',
            id: 'inDevelopment',
            icon: 'person-digging',
            color: findCategoryColourCode('In development'),
        },
    ];

    const rolesForDropdown: Array<IIdName> = [
        { id: 'AA', name: 'All roles' },
        { id: 'ownersForSearch', name: 'Owners' },
        { id: 'developersSearch', name: 'Developers' },
        { id: 'tradersForSearch', name: 'Traders' },
        { id: 'financersSearch', name: 'Financers' },
        { id: 'operatorsForSearch', name: 'Operators' },
        { id: 'technologyProvidersForSearch', name: 'Technology Providers' },
    ];

    const LATEST_UPDATE_OPTIONS: Array<IIdName> = [
        { id: '1', name: '1 month' },
        { id: '3', name: '3 months' },
        { id: '6', name: '6 months' },
    ];

    const segments: Array<IDecoratedDropdownItem> = [
        {
            name: 'All segments',
            id: 'AA',
            icon: 'asterisk',
            color: findCategoryColourCode('AA'),
        },
        {
            name: 'Front-of-meter',
            id: 'Front-of-meter',
            icon: 'utility-pole',
            color: findCategoryColourCode('Front-of-meter'),
        }, {
            name: 'Residential',
            id: 'Residential',
            icon: 'house-chimney-window',
            color: findCategoryColourCode('Residential'),

        }, {
            name: 'Commercial and Industrial (C&I)',
            id: 'Commercial and Industrial (C\u0026I)',
            icon: 'industry-windows',
            color: findCategoryColourCode('Commercial and Industrial (C&I)'),
        },
        {
            name: 'Unknown',
            id: 'Unknown',
            icon: 'question',
            color: findCategoryColourCode('Unknown'),
        },
    ];

    const installations: Array<IDecoratedDropdownItem> = [
        {
            name: 'All installations',
            id: 'AA',
            icon: 'asterisk',
            color: findCategoryColourCode('AA'),
        },
        {
            name: 'Standalone',
            id: 'Standalone',
            icon: 'battery-bolt',
            color: findCategoryColourCode('Standalone'),
        }, {
            name: 'Co-located',
            id: 'Co-located',
            icon: 'solar-panel',
            color: findCategoryColourCode('Co-located'),
        },
        {
            name: 'Unknown',
            id: 'Unknown',
            icon: 'question',
            color: findCategoryColourCode('Unknown'),
        },
    ];

    const actualVsForecastSwitchOptions: Array<IDecoratedDropdownItem> = [
        { name: 'EMMES Forecast', id: '2', description: 'Expected deployment of storage projects' },
        { name: 'Actual projects', id: '1', description: 'The sum of named projects and deployed aggregates' },
    ];

    const shortenedTechnologyList = computed<Array<IIdName>>(() => {
        const techList = [
            { id: 'AA', name: 'All Technologies' },
            { id: 'Li-ion battery', name: 'Li-ion Battery' },
            { id: 'Flow battery', name: 'Flow Battery' },
            { id: 'Other Electrochemical Storage', name: 'Other Electrochemical storage' },
            { id: 'Pumped hydro storage', name: 'Pumped Hydro Storage' },
            { id: 'Other Mechanical Storage', name: 'Other Mechanical storage' },
            { id: 'Thermal Storage', name: 'Thermal Storage' },
            { id: 'Unknown', name: 'Unknown' }];

        return techList;
    });

    const completeTechnologyListIDropdown = ref<Array<IDropdownItem>>([]);

    const technologies = ref<Array<IIdName>>([]);

    async function getTechnologies (): Promise<void> {
        const techs: Array<IDropdownItem> = await FiltersApi.getTechnologies();
        technologies.value = techs.map(({ value, name }) => ({
            id: value,
            name,
        })).sort((a, b) => {
            const aIndex = Object.keys(technologyMapping).indexOf(a.id);
            const bIndex = Object.keys(technologyMapping).indexOf(b.id);
            return aIndex - bIndex;
        });
    }

    const allCompanies = ref<Array<IIdName>>([]);

    function updateTechnologyList (list: Array<IDropdownItem>): void {
        completeTechnologyListIDropdown.value = list;
    }

    const technologyMapping: Record<string, string> = {
        'Li-ion battery': '',
        'LFP Li-ion battery': 'Li-ion battery',
        'LMO Li-ion battery': 'Li-ion battery',
        'LTO Li-ion battery': 'Li-ion battery',
        'NCA Li-ion battery': 'Li-ion battery',
        'NMC Li-ion battery': 'Li-ion battery',
        Battery: 'Li-ion battery',
        'Flow battery': '',
        'Iron redox flow battery': 'Flow battery',
        'Vanadium redox flow battery': 'Flow battery',
        'Other Electrochemical Storage': '',
        'Lead acid battery': 'Other Electrochemical Storage',
        'Sodium sulphur battery': 'Other Electrochemical Storage',
        'Zinc-bromine battery': 'Other Electrochemical Storage',
        'Super capacitor': 'Other Electrochemical Storage',
        'Pumped hydro storage': '',
        'Other Mechanical Storage': '',
        'Compressed air storage': 'Other Mechanical Storage',
        'Flywheel storage': 'Other Mechanical Storage',
        'Gravity storage': 'Other Mechanical Storage',
        'Liquid air storage': 'Other Mechanical Storage',
        'Liquid CO2 Storage': 'Other Mechanical Storage',
        'Thermal Storage': '',
        'Latent heat thermal storage': 'Thermal Storage',
        'Sensible heat thermal storage': 'Thermal Storage',
        'Thermochemical thermal storage': 'Thermal Storage',
        Unknown: 'Unknown',
    };

    const technologyMappingByKey: Record<string, string[]> = {};

    function setTechnologyMappingByKey (): void {
        Object.keys(technologyMapping).forEach((techKey: string) => {
            if (technologyMappingByKey[technologyMapping[techKey]] === undefined) {
                technologyMappingByKey[technologyMapping[techKey]] = [techKey];
            } else {
                technologyMappingByKey[technologyMapping[techKey]].push(techKey);
            }
        });
    }

    const completeTechnologyList = computed<IIdName[]>(() => (completeTechnologyListIDropdown.value.map((item: IDropdownItem) => ({ id: item.value, name: item.name }))));

    const valueStreamsByCountry = ref<Record<string, IDropdownItem[]>>({});

    async function loadValueStreamsForDropdown (): Promise<void> {
        if (Object.keys(valueStreamsByCountry.value).length !== 0) return;
        valueStreamsByCountry.value = await FiltersApi.getAllValueStreamsForDropdown();
    }

    async function loadCompanyNamesForDropdown (): Promise<void> {
        if (allCompanies.value.length !== 0) return;
        allCompanies.value = await FiltersApi.getAllCompanies();
    }

    const countriesWithValueStreams = computed<Array<string>>(() => {
        const countryCodes: Array<string> = Object.keys(valueStreamsByCountry.value);
        countryCodes.unshift('AA');
        return countryCodes;
    });

    const valueStreamsForDropdown = computed<Array<IDecoratedDropdownItem>>(() => {
        const filteredValueStreams: Array<IDecoratedDropdownItem> = [{ name: 'All projects', id: 'AA' }, { name: 'Any value stream', id: anyValueStreamValue }];

        const countriesToUse: Array<string> = selectedCountries.value[0] === 'AA' ? countriesWithValueStreams.value : selectedCountries.value;

        countriesToUse.forEach((country) => {
            const valueStreams = valueStreamsByCountry.value[country];

            valueStreams?.forEach((stream) => {
                const alreadyHaveStream = filteredValueStreams.find(x => x.name === stream.name);
                if (!alreadyHaveStream) {
                    filteredValueStreams.push({ name: stream.name, id: stream.value });
                }
            });
        });

        return filteredValueStreams;
    });

    const neutralFilters : IFilters = {
        countries: ['AA'],
        technologies: ['AA'],
        segments: ['AA'],
        installations: ['AA'],
        operational: ['AA'],
        pumpedStorage: true,
        actual: 0,
    };

    const countryList = ref<Array<IDropdownItem>>([]);
    const countryNameById = ref<Record<string, string>>({});

    const maxStorageCapacity = ref<number>(0);
    const maxPowerCapacity = ref<number>(0);
    const maxDurationCapacity = ref<number>(0);

    const selectedCountries = ref<Array<string>>([]);
    const selectedSegments = ref<Array<string>>([]);
    const selectedRoles = ref<Array<string>>([]);
    const selectedInstallations = ref<Array<string>>([]);
    const selectedOperational = ref<Array<string>>(['AA']);
    const selectedTechnologies = ref<Array<string>>(['AA']);
    const selectedValueStreams = ref<Array<string>>(['AA']);
    const selectedMissingRole = ref<string>('AA');
    const selectedYear = ref<string>('2022');
    const projectNameFilter = ref<string>('');
    const companyName = ref<string>('');
    const constructionDate = ref<Array<number>>([startDate, endDate]);
    const operationDate = ref<Array<number>>([startDate, endDate]);
    const sourcesMonthRange = ref<number>(1);
    const powerCapacityRange = ref<Array<number>>([0, 0]);
    const storageCapacityRange = ref<Array<number>>([0, 0]);
    const durationRange = ref<Array<number>>([0, 0]);
    const usePumpedStorage = ref<boolean>(false);
    const useOperationDate = ref<boolean>(false);
    const useSourcesMonthRange = ref<boolean>(false);
    const useConstructionDate = ref<boolean>(false);
    const usePowerCapacity = ref<boolean>(false);
    const useStorageCapacity = ref<boolean>(false);
    const useDuration = ref<boolean>(false);
    const selectedCompanies = ref<Array<string>>([]);

    const filters = computed<IFilters>(() => {
        const result: IFilters = {
            countries: selectedCountries.value,
            technologies: selectedTechnologies.value,
            segments: selectedSegments.value,
            installations: selectedInstallations.value,
            operational: selectedOperational.value,
            pumpedStorage: usePumpedStorage.value,
            actual: 0,
            usePowerCapacityRange: usePowerCapacity.value,
            powerCapacityRange: { minimum: powerCapacityRange.value[0], maximum: powerCapacityRange.value[1] },
            useStorageCapacityRange: useStorageCapacity.value,
            storageCapacityRange: { minimum: storageCapacityRange.value[0], maximum: storageCapacityRange.value[1] },
            useDurationRange: useDuration.value,
            durationRange: { minimum: durationRange.value[0], maximum: durationRange.value[1] },
            useOperationalDateRange: useOperationDate.value,
            operationalDateRange: { minimum: operationDate.value[0], maximum: operationDate.value[1] },
            useUnderConstructionDateRange: useConstructionDate.value,
            underConstructionDateRange: { minimum: constructionDate.value[0], maximum: constructionDate.value[1] },
            useLatestUpdateWithin: useSourcesMonthRange.value,
            latestUpdateWithin: sourcesMonthRange.value,
            valueStreams: selectedValueStreams.value,
            projectsLookingForA: selectedMissingRole.value,
            companiesInvolved: selectedCompanies.value,
            companyRoles: selectedRoles.value,
        };
        return result;
    });

    const countryListIIdname = computed<Array<IDecoratedDropdownItem>>(() => {
        const listOfCountries: Array<IDecoratedDropdownItem> = [];
        countryList.value.forEach((country: IDropdownItem) => {
            const objectToAdd: IIdName = {
                name: country.name,
                id: country.value,
            };
            listOfCountries.push(objectToAdd);
        });
        return listOfCountries;
    });

    function updateCountryNameById (): void {
        const newCountryList: Record<string, string> = {};
        countryList.value.forEach(country => {
            if (typeof country.value === 'string') {
                newCountryList[country.value] = country.name;
            }
        });
        countryNameById.value = newCountryList;
    }

    function updateCountryList (list: Array<IDropdownItem>): void {
        countryList.value = list;
        updateCountryNameById();
    }

    const actualVsForecastDefault: string = '2';

    function resetDefaults (): void {
        selectedCountries.value = ['AA'];
        selectedSegments.value = ['AA'];
        selectedInstallations.value = ['AA'];
        selectedOperational.value = ['AA'];
        selectedTechnologies.value = ['AA'];
        selectedRoles.value = ['AA'];
        selectedYear.value = '2022';
        selectedMissingRole.value = 'AA';
        selectedValueStreams.value = ['AA'];
        selectedCompanies.value = [];
        projectNameFilter.value = '';
        companyName.value = '';
        sourcesMonthRange.value = 1;
        constructionDate.value = [startDate, endDate];
        operationDate.value = [startDate, endDate];
        useConstructionDate.value = false;
        useOperationDate.value = false;
        useSourcesMonthRange.value = false;
        usePowerCapacity.value = false;
        useStorageCapacity.value = false;
        useDuration.value = false;
    }

    function updateSelectedTechnologies (newTechs: Array<string>): void {
        selectedTechnologies.value = newTechs;
    }

    function updateSelectedCompanies (newCompanies: Array<string>): void {
        if (newCompanies.includes('AA')) {
            selectedCompanies.value = [];
            return;
        }
        selectedCompanies.value = newCompanies;
    }

    function updateSelectedCountries (newCountries: Array<string>): void {
        selectedCountries.value = newCountries;
    }

    function updateSelectedValueStreams (newStreams: Array<string>): void {
        selectedValueStreams.value = newStreams;
    }

    function updateSelectedSegment (newSegment: string): void {
        selectedSegments.value = [newSegment];
    }

    function updateSelectedInstallation (newInstallation: string): void {
        selectedInstallations.value = [newInstallation];
    }

    function updateSelectedOperationalOption (newSelection: string): void {
        selectedOperational.value = [newSelection];
    }

    function updateSelectedRoles (newSelection: Array<string>): void {
        selectedRoles.value = newSelection;
    }

    function updateSelectedMissingRole (newSelection: string): void {
        selectedMissingRole.value = newSelection;
    }

    function updateSourcesMonthRange (val: number): void {
        sourcesMonthRange.value = val;
    }

    function updateConstructionDate (val: Array<number>): void {
        constructionDate.value = val;
    }

    function updateOperationDate (val: Array<number>): void {
        operationDate.value = val;
    }

    function updatePowerCapacityRange (val: Array<number>): void {
        powerCapacityRange.value = val;
    }

    function updateStorageCapacityRange (val: Array<number>): void {
        storageCapacityRange.value = val;
    }

    function updateDurationRange (val: Array<number>): void {
        durationRange.value = val;
    }

    function updateUseSourcesMonthRange (bool: boolean): void {
        useSourcesMonthRange.value = bool;
    }

    function updateUseConstructionDate (bool: boolean): void {
        useConstructionDate.value = bool;
    }

    function updateUseOperrationDate (bool: boolean): void {
        useOperationDate.value = bool;
    }

    function updateUseStorageCapacity (bool: boolean): void {
        useStorageCapacity.value = bool;
    }

    function updateUsePowerCapacity (bool: boolean): void {
        usePowerCapacity.value = bool;
    }

    function updateUseDuration (bool: boolean): void {
        useDuration.value = bool;
    }

    function filterCheckValueStream (row: ICsvData): boolean {
        if (selectedValueStreams.value[0] === 'AA') return true;
        if (!row.valueStreams || row.valueStreams.length === 0) return false;
        if (selectedValueStreams.value.includes(anyValueStreamValue)) return true;
        return selectedValueStreams.value.findIndex((stream) => row.valueStreams!.includes(stream)) > -1;
    }

    async function initialise (): Promise<void> {
        await getTechnologies();
        setTechnologyMappingByKey();
    }

    initialise();

    return {
        actualVsForecastSwitchOptions,
        actualVsForecastDefault,
        allCompanies,
        countryList,
        countryListIIdname,
        countriesWithValueStreams,
        countryNameById,
        companyName,
        constructionDate,
        durationRange,
        filters,
        sourcesMonthRange,
        sliderEndDate,
        endDate,
        technologies,
        segments,
        installations,
        projectTypeOptions,
        LATEST_UPDATE_OPTIONS,
        operationalOptions,
        completeTechnologyList,
        shortenedTechnologyList,
        valueStreamsForDropdown,
        operationDate,
        powerCapacityRange,
        projectNameFilter,
        startDate,
        technologyMapping,
        technologyMappingByKey,
        updateCountryList,
        updateTechnologyList,
        loadCompanyNamesForDropdown,
        loadValueStreamsForDropdown,
        maxDurationCapacity,
        maxPowerCapacity,
        maxStorageCapacity,
        neutralFilters,
        resetDefaults,
        rolesForDropdown,
        selectedCompanies,
        selectedCountries,
        selectedSegments,
        selectedInstallations,
        selectedOperational,
        selectedTechnologies,
        selectedRoles,
        selectedValueStreams,
        selectedMissingRole,
        selectedYear,
        sliderStartDate,
        storageCapacityRange,
        filterCheckValueStream,
        updateConstructionDate,
        updateDurationRange,
        updateSourcesMonthRange,
        updateOperationDate,
        updatePowerCapacityRange,
        updateSelectedCompanies,
        updateSelectedCountries,
        updateSelectedInstallation,
        updateSelectedMissingRole,
        updateSelectedOperationalOption,
        updateSelectedRoles,
        updateSelectedSegment,
        updateSelectedTechnologies,
        updateSelectedValueStreams,
        updateStorageCapacityRange,
        updateUseConstructionDate,
        updateUseDuration,
        updateUseSourcesMonthRange,
        updateUseOperrationDate,
        updateUsePowerCapacity,
        updateUseStorageCapacity,
        useConstructionDate,
        useDuration,
        useSourcesMonthRange,
        useOperationDate,
        usePowerCapacity,
        usePumpedStorage,
        useStorageCapacity,
    };
});
